import { useState, useEffect, useCallback } from "react";
import { CheckCircle2, AlertTriangle, ShieldAlert, ClipboardCheck, Loader2, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { formatDate } from "../lib/utils-format";
import api from "../lib/api";

const STYLE = {
  GREEN: { cls: "text-emerald-600 bg-emerald-500/10 border-emerald-500/30", bar: "bg-emerald-500", Icon: CheckCircle2, text: "GO", label: "Clear to Proceed" },
  YELLOW: { cls: "text-amber-600 bg-amber-500/10 border-amber-500/30", bar: "bg-amber-500", Icon: AlertTriangle, text: "CAUTION", label: "Conditional — Open Items" },
  RED: { cls: "text-red-600 bg-red-500/10 border-red-500/30", bar: "bg-red-500", Icon: ShieldAlert, text: "NO-GO", label: "Do Not Proceed Yet" },
};

function live(baseline, done, total) {
  const b = (baseline || "").toUpperCase();
  if (total === 0) return b || "YELLOW";
  if (done >= total) return "GREEN";
  if (b === "RED") return "RED";
  return "YELLOW";
}

// Generic GO/CAUTION/NO-GO gate card (Feasibility, Financial Review, …) with a live
// task-completion progress bar. `source` matches the tasks created by that gate.
export function GateResults({ title, tid, source, analysis, analyzedAt, loading, onRun, busy, recordId, refreshKey, runningText }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);

  const loadCounts = useCallback(() => {
    if (!recordId) return;
    api.get(`/tasks?record_id=${recordId}&limit=300`)
      .then((r) => {
        const g = (r.data.items || []).filter((t) => t.source === source);
        setTotal(g.length);
        setDone(g.filter((t) => t.status === "Completed").length);
      })
      .catch(() => {});
  }, [recordId, source]);
  useEffect(() => { loadCounts(); }, [loadCounts, refreshKey, analyzedAt]);

  const effective = live(analysis?.verdict, done, total);
  const { cls, bar, Icon, text, label } = STYLE[effective] || STYLE.YELLOW;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const baseText = STYLE[(analysis?.verdict || "").toUpperCase()]?.text;
  const changed = baseText && baseText !== text;

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid={`${tid}-card`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full px-6 py-3.5 border-b border-border bg-secondary/30 flex items-center justify-between gap-3 text-left hover:bg-secondary/50 transition-colors" data-testid={`${tid}-toggle`}>
        <span className="flex items-center gap-2.5 min-w-0">
          <span className="text-sm font-semibold">{title}</span>
          {analysis?.verdict && !open && (
            <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${cls}`}>{text}</span>
          )}
        </span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (loading ? (
        <div className="flex items-center gap-3 py-10 justify-center text-sm text-muted-foreground" data-testid={`${tid}-loading`}>
          <Loader2 className="h-5 w-5 animate-spin" />{runningText || "Angela is analyzing…"}
        </div>
      ) : analysis ? (
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${cls}`} data-testid={`${tid}-verdict`}>
              <Icon className="h-6 w-6 shrink-0" />
              <div>
                <div className="text-lg font-bold leading-none">{text}</div>
                <div className="text-xs opacity-80 mt-1">{label}</div>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={onRun} disabled={busy} data-testid={`${tid}-rerun-btn`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ClipboardCheck className="h-3.5 w-3.5 mr-1.5" />}Re-run
            </Button>
          </div>

          {total > 0 && (
            <div data-testid={`${tid}-progress`}>
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="font-semibold uppercase tracking-wide text-muted-foreground">Progress</span>
                <span className="text-muted-foreground"><span className="font-semibold text-foreground">{done} of {total}</span> complete · {pct}%</span>
              </div>
              <div className="h-2.5 rounded-full bg-secondary overflow-hidden">
                <div className={`h-full ${bar} transition-all duration-500`} style={{ width: `${pct}%` }} />
              </div>
              {changed && (
                <p className="text-[11px] text-muted-foreground/80 mt-1.5">
                  {done >= total ? `All items cleared — upgraded from Angela's initial ${baseText}.` : `Live status as items close · Angela's initial verdict: ${baseText}.`}
                </p>
              )}
            </div>
          )}

          {analysis.headline && <p className="text-sm font-medium">{analysis.headline}</p>}
          {analysis.summary && <p className="text-sm text-muted-foreground leading-relaxed">{analysis.summary}</p>}
          {(analysis.tasks_created > 0 || analysis.tasks_skipped > 0) && (
            <div className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{analysis.tasks_created || 0}</span> new task{analysis.tasks_created === 1 ? "" : "s"} created
              {analysis.tasks_skipped > 0 && <> · {analysis.tasks_skipped} skipped as duplicates</>}
              . See the Tasks section for step-by-step actions.
            </div>
          )}
          {analysis.key_risks?.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-amber-600 mb-1.5">Key Risks</div>
              <ul className="space-y-1">{analysis.key_risks.map((s, i) => <li key={i} className="text-sm text-muted-foreground flex gap-2"><span className="text-amber-500">!</span>{s}</li>)}</ul>
            </div>
          )}
          {analysis.recommendation && (
            <div className="rounded-lg bg-secondary/50 border border-border px-4 py-3">
              <div className="text-xs font-semibold uppercase tracking-wide mb-1">Recommendation</div>
              <p className="text-sm text-muted-foreground leading-relaxed">{analysis.recommendation}</p>
            </div>
          )}
          {analyzedAt && <p className="text-[11px] text-muted-foreground/70">Last run {formatDate(analyzedAt)}</p>}
        </div>
      ) : (
        <div className="px-6 py-6 space-y-3" data-testid={`${tid}-empty`}>
          <p className="text-sm text-muted-foreground">Have Angela evaluate this gate, produce a GO / CAUTION / NO-GO verdict, and create step-by-step tasks.</p>
          <Button size="sm" variant="outline" onClick={onRun} disabled={busy} data-testid={`${tid}-run-btn`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ClipboardCheck className="h-3.5 w-3.5 mr-1.5" />}Run {title}
          </Button>
        </div>
      ))}
    </div>
  );
}
